<template>
  <div :class="$b({ unsetBackground })">
    <canvas
      v-show="showBackground"
      :id="backgroundId"
      :key="backgroundId"
      :class="$b('bg')"
      :width="width"
      :height="height"
      style="width: 100%"
    />
    <div :class="$b('product-wrapper', { fullSize })">
      <canvas
        :id="midgroundId"
        :key="midgroundId"
        :class="$b('product')"
        :width="width"
        :height="height"
        style="width: 100%"
      />
      <canvas
        :id="irisId"
        :key="irisId"
        :class="$b('product', { iris: true, visible: showIrisPreview })"
        :width="width"
        :height="height"
        style="width: 100%"
      />
      <canvas
        v-show="showSecondary"
        :id="secondaryId"
        :key="secondaryId"
        :class="
          $b('product', { secondary: true, minimised: secondaryMinimised })
        "
        :width="width"
        :height="height"
        style="width: 100%"
      />
    </div>

    <div :class="$b('limited-edition-logo')">
      <DatoCmsImage
        v-if="limitedEditionLogo"
        :key="limitedEditionLogo.responsiveImage.src"
        :data="{
          ...limitedEditionLogo.responsiveImage,
          sizes: '(max-width: 800px) 50vw, 400px',
        }"
        :lazy-load="false"
      />
    </div>
  </div>
</template>

<script>
import { Image as DatoCmsImage } from 'vue-datocms'

export default {
  name: 'ThreeSixtyCanvas',
  components: {
    DatoCmsImage,
  },
  props: {
    width: { type: Number, default: 1400 },
    height: { type: Number, default: 1400 },
    showBackground: { type: Boolean, default: true },
    unsetBackground: { type: Boolean, default: false },
    showReflections: { type: Boolean, default: true },
    showSecondary: { type: Boolean, default: true },
    fullSize: { type: Boolean, default: false },
    secondaryMinimised: { type: Boolean, default: false },
    showIrisPreview: { type: Boolean, default: false },
    limitedEditionLogo: { type: Object, default: () => {} },
  },
  data: () => ({
    mgCanvas: null,
    mgCtx: null,
    secondaryCanvas: null,
    secondaryCtx: null,
    irisCanvas: null,
    irisCtx: null,
    bgCanvas: null,
    bgCtx: null,
    ready: false,
    layers: [],
  }),
  computed: {
    midgroundId() {
      return 'canvas-mg'
    },
    secondaryId() {
      return 'canvas-secondary'
    },
    irisId() {
      return 'canvas-iris'
    },
    backgroundId() {
      return 'canvas-bg'
    },
  },
  updated() {
    this.render(this.layers)
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
    this.getCanvases()
  },
  unmounted() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    getCanvases() {
      if (this.ready) return

      this.mgCanvas = this.$el.querySelector(`#${this.midgroundId}`)
      this.mgCtx = this.mgCanvas?.getContext('2d')

      this.secondaryCanvas = this.$el.querySelector(`#${this.secondaryId}`)
      this.secondaryCtx = this.secondaryCanvas?.getContext('2d')

      this.irisCanvas = this.$el.querySelector(`#${this.irisId}`)
      this.irisCtx = this.irisCanvas?.getContext('2d')

      this.bgCanvas = this.$el.querySelector(`#${this.backgroundId}`)
      this.bgCtx = this.bgCanvas?.getContext('2d')

      if (
        !this.mgCtx ||
        !this.bgCtx ||
        !this.irisCanvas ||
        !this.secondaryCanvas
      ) {
        setTimeout(this.getCanvases, 100)
      } else {
        this.ready = true
      }
    },
    async render(layers, scale = 100, xOffset = 0, yOffset = 0) {
      this.clear()

      await Promise.all(
        layers.map((layer) => {
          if (layer.isBackground) {
            return this.renderScene(layer)
          }

          if (layer.isReflection && !this.showReflections) return null
          return this.renderProduct(layer, scale, xOffset, yOffset)
        })
      )

      this.layers = layers
    },
    async renderScene(layer) {
      if (!layer.img) await layer.loadCurrentFrame()
      if (!layer.img || layer.currentFrameDisabled) return

      // If the canvas is not ready, wait for it to be ready
      if (!this.ready) {
        console.log('canvas not ready for scene')
        return requestAnimationFrame(() => this.renderScene(layer))
      }

      if (!this.bgCtx) this.getCanvases()
      if (this.ready && !this.bgCtx)
        throw new Error('Unable to get canvas context')

      // Render background at full width and height
      const context = this.bgCtx
      const canvas = context.canvas

      const sourceRatio = layer.img.width / layer.img.height
      const destRatio = canvas.width / canvas.height
      let width, height
      if (sourceRatio > destRatio) {
        // Match height
        width = sourceRatio * canvas.height
        height = canvas.height
      } else {
        // Match width
        width = canvas.width
        height = (1 / sourceRatio) * canvas.width
      }

      const offsetX = (canvas.width - width) / 2
      const offsetY = (canvas.height - height) / 2
      context?.drawImage(
        layer.img,
        0, // Source
        0,
        layer.img.width,
        layer.img.height,
        offsetX, // Destination
        offsetY,
        width,
        height
      )
    },
    async renderProduct(layer, scale, xOffset, yOffset) {
      if (!layer.img) await layer.loadCurrentFrame()
      if (!layer.img || layer.currentFrameDisabled) return

      // If the canvas is not ready, wait for it to be ready
      if (!this.ready) {
        console.log('canvas not ready for product')
        return requestAnimationFrame(() =>
          this.renderProduct(layer, scale, xOffset, yOffset)
        )
      }

      let context = this.mgCtx
      if (layer.isSecondary) context = this.secondaryCtx
      if (layer.isIrisMask) context = this.irisCtx
      if (!context) this.getCanvases()
      if (this.ready && !context)
        throw new Error('Unable to get canvas context')

      // Render product scaled
      const scalingFactor =
        Math.min(this.height / layer.img.height, this.width / layer.img.width) *
        (scale / 100)

      const offsetX =
        (this.width - layer.img.width * scalingFactor) / 2 +
        xOffset * scalingFactor
      const offsetY =
        (this.height - layer.img.height * scalingFactor) / 2 +
        yOffset * scalingFactor

      context.globalAlpha = layer.opacity || 1
      context?.drawImage(
        layer.img, // Source img
        0, // Source x
        0, // Source y
        layer.img.width, // Source width
        layer.img.height, // Source height
        offsetX, // Dest x
        offsetY, // Dest y
        layer.img.width * scalingFactor, // Dest width
        layer.img.height * scalingFactor // Dest height
      )
    },
    clear() {
      this.mgCtx?.clearRect(0, 0, this.mgCanvas?.width, this.mgCanvas?.height)
      if (this.mgCtx) this.mgCtx.imageSmoothingEnabled = true

      this.secondaryCtx?.clearRect(
        0,
        0,
        this.secondaryCanvas?.width,
        this.secondaryCanvas?.height
      )
      if (this.secondaryCtx) this.secondaryCtx.imageSmoothingEnabled = true

      this.irisCtx?.clearRect(
        0,
        0,
        this.irisCanvas?.width,
        this.irisCanvas?.height
      )
      if (this.irisCtx) this.irisCtx.imageSmoothingEnabled = true

      this.bgCtx?.clearRect(0, 0, this.bgCanvas?.width, this.bgCanvas?.height)
      if (this.bgCtx) this.bgCtx.imageSmoothingEnabled = true
    },
    handleResize() {
      this.render(this.layers)
    },
  },
}
</script>

<style lang="postcss">
.ThreeSixtyCanvas {
  @apply relative
    w-full
    flex items-center content-center
    overflow-hidden
    mdMax:h-full;

  @screen md {
    border-radius: 10px;

    &::before {
      float: left;
      padding-top: 100%;
      content: '';
    }

    &::after {
      display: block;
      content: '';
      clear: both;
    }
  }

  &--unset-background {
    background-color: unset;
  }
}

.ThreeSixtyCanvas__bg {
  @apply top-0 left-0
    w-full h-full
    absolute;
}

.ThreeSixtyCanvas__product-wrapper {
  @apply relative w-full h-full mx-auto;
  max-width: 1500px;
}

.ThreeSixtyCanvas__product {
  @apply absolute
    top-1/2 left-1/2
    -translate-x-1/2 -translate-y-1/2
    w-full
    mdMax:pb-sm;
  max-width: 1300px;

  &--secondary {
    @apply -translate-x-[43%] -translate-y-[35%] scale-90 transition-transform duration-300 ease-in-out;

    &.ThreeSixtyCanvas__product--minimised {
      @apply -translate-x-[25%] -translate-y-[15%] scale-50;
    }
  }

  &--iris {
    @apply opacity-0
      transition-opacity duration-1000;

    &.ThreeSixtyCanvas__product--visible {
      @apply opacity-30;
    }
  }
}

.ThreeSixtyCanvas__product-wrapper--full-size {
  max-width: 100%;
  max-height: 100%;
  overflow: hidden;
}

.ThreeSixtyCanvas__product-wrapper--full-size .ThreeSixtyCanvas__product {
  max-width: 2100px;
  height: 100%;
}

.ThreeSixtyCanvas__limited-edition-logo {
  @apply absolute w-1/4
    bottom-[40px] right-sm lg:bottom-md lg:right-md;

  picture {
    @apply object-cover;
  }
}
</style>
